// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { config } from '@iheartjane/config';
import { Flex, Typography, useToast } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { FontPicker, TextWithFont } from '@jane/business-admin/components';
import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import {
  businessPaths,
  toBloomFontFamily,
  toLabeledFontOption,
} from '@jane/business-admin/util';
import type { GoogleFontFamily } from '@jane/shared-ecomm/data-access';
import { useGoogleFonts } from '@jane/shared-ecomm/data-access';
import { loadFont } from '@jane/shared/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';

const BorderedFlex = styled(Flex)(({ theme }) => ({
  borderBottom: `1px solid ${theme.colors.grays.light}`,
}));

export const PremiumFonts = () => {
  const navigate = useNavigate();
  const fontQuery = useGoogleFonts({ apiKey: config.googleFontsKey });
  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isLoading } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );
  const toast = useToast();

  const supportedFontFamilies = useMemo(
    () => fontQuery.data ?? [],
    [fontQuery.data]
  );

  const formMethods = useForm<{
    fontUpdates: {
      body: Nullable<GoogleFontFamily>;
      heading: Nullable<GoogleFontFamily>;
    };
  }>({
    defaultValues: {
      fontUpdates: {
        heading: supportedFontFamilies.find(
          ({ family }) =>
            family ===
            bloomMenuQuery.data?.theme?.bloom?.fonts?.heading?.font_family
        ),
        body: supportedFontFamilies.find(
          ({ family }) =>
            family ===
            bloomMenuQuery.data?.theme?.bloom?.fonts?.body?.font_family
        ),
      },
    },
  });
  const { control } = formMethods;
  const fontUpdates = useWatch({ control, name: 'fontUpdates' });

  useEffect(() => {
    loadFont({
      fontFamily: fontUpdates.heading?.family,
      url: fontUpdates.heading?.files['regular'],
    });
    loadFont({
      fontFamily: fontUpdates.body?.family,
      url: fontUpdates.body?.files['regular'],
    });
  }, [JSON.stringify(fontUpdates)]);

  const publish = useCallback(() => {
    updateBloomMenu(
      {
        theme: {
          ...bloomMenuQuery.data?.theme?.bloom,
          fonts: {
            heading: toBloomFontFamily(fontUpdates.heading),
            body: toBloomFontFamily(fontUpdates.body),
          },
        },
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          toast.add({
            label: 'Menu updated!',
            variant: 'success',
          });
          navigate(businessPaths.premium());
        },
      }
    );
  }, [
    bloomMenuQuery.data?.theme?.bloom,
    toast,
    updateBloomMenu,
    fontUpdates,
    navigate,
  ]);

  const preview = useCallback(() => {
    updateBloomMenu(
      {
        theme_draft: {
          ...bloomMenuQuery.data?.theme?.bloom,
          fonts: {
            heading: toBloomFontFamily(fontUpdates.heading),
            body: toBloomFontFamily(fontUpdates.body),
          },
        },
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          if (!bloomMenuQuery.data?.menu_url) {
            return;
          }
          const previewUrl = new URL(bloomMenuQuery.data.menu_url);
          previewUrl.searchParams.set('draft', 'true');
          const newWindow = window.open(
            previewUrl,
            '_blank',
            'noopener,noreferrer'
          );
          if (newWindow) {
            newWindow.opener = null;
          }
        },
      }
    );
  }, [
    bloomMenuQuery.data?.menu_url,
    bloomMenuQuery.data?.theme?.bloom,
    fontUpdates,
    toast,
    updateBloomMenu,
  ]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isLoading}
      onSubmit={publish}
      preview={{
        onClick: preview,
        disable: isLoading,
      }}
      title="Fonts"
    >
      {bloomMenuQuery.isSuccess && (
        <BorderedFlex
          background="grays-white"
          p={40}
          flexDirection="row"
          gap={48}
        >
          <Flex flexDirection="column" gap={24} width="50%">
            <Flex flexDirection="column" gap={8}>
              <Typography variant="body-bold">Headings</Typography>

              <Controller
                control={control}
                name="fontUpdates.heading"
                render={({ field }) => (
                  <FontPicker
                    activeFontFamily={toLabeledFontOption(
                      supportedFontFamilies.find(
                        ({ family }) => family === field.value?.family
                      )
                    )}
                    defaultFontFamily={{
                      label: 'Euclid Circular B (Default)',
                      value: 'Euclid Circular B',
                    }}
                    fontFamilies={supportedFontFamilies}
                    label="heading-font"
                    onChange={(font) => {
                      field.onChange(font);
                    }}
                  />
                )}
              />
            </Flex>
            <Flex flexDirection="column" gap={8}>
              <Typography variant="body-bold">Paragraph</Typography>
              <Controller
                control={control}
                name="fontUpdates.body"
                render={({ field }) => (
                  <FontPicker
                    activeFontFamily={toLabeledFontOption(
                      supportedFontFamilies.find(
                        ({ family }) => family === field.value?.family
                      )
                    )}
                    defaultFontFamily={{
                      label: 'Source Sans Pro (Default)',
                      value: 'Jane Default',
                    }}
                    fontFamilies={supportedFontFamilies}
                    label="body-font"
                    onChange={(font) => {
                      field.onChange(font);
                    }}
                  />
                )}
              />
            </Flex>
          </Flex>

          <Flex flexDirection="column" gap={8} width="50%">
            <Typography variant="body-bold">Preview</Typography>
            <Flex
              p={16}
              borderRadius="sm"
              background="grays-ultralight"
              flexDirection="column"
              justifyContent="center"
              height="100%"
            >
              <TextWithFont
                fontFamily={fontUpdates.heading?.family}
                pb={16}
                variant="title"
              >
                Heading
              </TextWithFont>

              <TextWithFont
                fontFamily={fontUpdates.body?.family}
                variant="body"
              >
                This is how paragraph text will appear
              </TextWithFont>
            </Flex>
          </Flex>
        </BorderedFlex>
      )}
    </ModalPageWrapper>
  );
};
